/**
 * Almacén local del navegador, sobre IndexedDB.
 *
 * Se escribe a mano, sin librerías, por dos razones: son unas doscientas líneas
 * bien entendidas, y una dependencia más en la capa que guarda el dinero es una
 * dependencia más que puede romperse sin que nos enteremos.
 *
 * Guarda **una copia de todo** y una **cola de lo que falta por enviar**. La copia
 * es lo que permite abrir la aplicación sin conexión y ver tus movimientos; la
 * cola, lo que permite registrar uno en la calle y que llegue cuando vuelva la red.
 */

const DB_NAME = 'eldescuadre';
const DB_VERSION = 1;

/** Los almacenes. Cada uno guarda registros por su identificador. */
export const STORES = {
  /** Copia local de las entidades del servidor, por tipo. */
  accounts: 'accounts',
  categories: 'categories',
  transactions: 'transactions',
  transactionItems: 'transactionItems',
  budgets: 'budgets',
  debts: 'debts',
  debtPayments: 'debtPayments',
  fiadoStores: 'fiadoStores',
  fiadoEntries: 'fiadoEntries',
  recurringRules: 'recurringRules',
  splits: 'splits',
  goals: 'goals',
  goalContributions: 'goalContributions',
  tags: 'tags',
  /** Operaciones pendientes de enviar, en orden. */
  outbox: 'outbox',
  /** Metadatos: el cursor de sincronización, la última vez que se sincronizó. */
  meta: 'meta',
} as const;

export type StoreName = (typeof STORES)[keyof typeof STORES];

/** Índices secundarios que hacen falta para consultar sin conexión. */
const INDEXES: Partial<Record<StoreName, ReadonlyArray<{ name: string; keyPath: string }>>> = {
  transactions: [
    { name: 'by-date', keyPath: 'date' },
    { name: 'by-account', keyPath: 'accountId' },
    { name: 'by-category', keyPath: 'categoryId' },
  ],
  debtPayments: [{ name: 'by-debt', keyPath: 'debtId' }],
  fiadoEntries: [{ name: 'by-store', keyPath: 'storeId' }],
  outbox: [{ name: 'by-queued', keyPath: 'queuedAt' }],
};

let dbPromise: Promise<IDBDatabase> | null = null;

/** Abre la base, creando los almacenes la primera vez. */
export function openDatabase(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('Este navegador no permite guardar datos sin conexión.'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      for (const name of Object.values(STORES)) {
        if (!db.objectStoreNames.contains(name)) {
          const store = db.createObjectStore(name, { keyPath: 'id' });
          for (const index of INDEXES[name] ?? []) {
            store.createIndex(index.name, index.keyPath, { unique: false });
          }
        }
      }
    };

    request.onsuccess = () => {
      const db = request.result;

      // Si otra pestaña actualiza el esquema, se cierra esta conexión para que no
      // se quede bloqueada y la otra pueda trabajar.
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };

      resolve(db);
    };

    request.onerror = () => reject(request.error ?? new Error('No se pudo abrir el almacén.'));
  });

  return dbPromise;
}

/** Ejecuta una operación dentro de una transacción y espera a que se confirme. */
async function withStore<T>(
  storeName: StoreName,
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T> | null,
): Promise<T | undefined> {
  const db = await openDatabase();

  return new Promise<T | undefined>((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    const request = action(store);

    let result: T | undefined;

    if (request) {
      request.onsuccess = () => {
        result = request.result;
      };
    }

    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => reject(transaction.error ?? new Error('Fallo en el almacén.'));
    transaction.onabort = () => reject(transaction.error ?? new Error('Operación cancelada.'));
  });
}

/** Guarda uno o varios registros, sobrescribiendo por identificador. */
export async function putAll<T extends { id: string }>(
  storeName: StoreName,
  records: readonly T[],
): Promise<void> {
  if (records.length === 0) return;

  await withStore(storeName, 'readwrite', (store) => {
    for (const record of records) store.put(record);
    return null;
  });
}

/** Guarda un registro. */
export async function put<T extends { id: string }>(
  storeName: StoreName,
  record: T,
): Promise<void> {
  await putAll(storeName, [record]);
}

/** Lee un registro por su identificador. */
export async function get<T>(storeName: StoreName, id: string): Promise<T | undefined> {
  return withStore<T>(storeName, 'readonly', (store) => store.get(id));
}

/** Lee todos los registros de un almacén. */
export async function getAll<T>(storeName: StoreName): Promise<T[]> {
  const result = await withStore<T[]>(storeName, 'readonly', (store) => store.getAll());
  return result ?? [];
}

/** Lee por un índice. */
export async function getAllByIndex<T>(
  storeName: StoreName,
  indexName: string,
  value: IDBValidKey,
): Promise<T[]> {
  const result = await withStore<T[]>(storeName, 'readonly', (store) =>
    store.index(indexName).getAll(value),
  );
  return result ?? [];
}

/** Borra registros por identificador. */
export async function removeMany(storeName: StoreName, ids: readonly string[]): Promise<void> {
  if (ids.length === 0) return;
  await withStore(storeName, 'readwrite', (store) => {
    for (const id of ids) store.delete(id);
    return null;
  });
}

/** Vacía un almacén. */
export async function clear(storeName: StoreName): Promise<void> {
  await withStore(storeName, 'readwrite', (store) => store.clear());
}

/** Guarda un valor de metadatos (el cursor, la última sincronización…). */
export async function setMeta(key: string, value: unknown): Promise<void> {
  await withStore(STORES.meta, 'readwrite', (store) => store.put({ id: key, value }));
}

/** Lee un valor de metadatos. */
export async function getMeta<T>(key: string): Promise<T | undefined> {
  const record = await get<{ id: string; value: T }>(STORES.meta, key);
  return record?.value;
}

/** Borra todo. Se usa al cerrar sesión, para no dejar datos de otro en el equipo. */
export async function wipeEverything(): Promise<void> {
  for (const name of Object.values(STORES)) {
    await clear(name);
  }
}
